import React from 'react';
import './slider.css';
import { SliderProps } from '../../../types/DesignSystem/Slider';
import { ControlSize } from '../../../types/DesignSystem/controlSize';
import FieldTitle from '../FieldTitle/FieldTitle';
import Typography from '../Typography/Typography';
import { FOCUS_RING } from '../focusRing';
import { withClassName } from '../cn';
import { useControllableState } from '../useControllableState';

/**
 * Track thickness and thumb diameter, on the control-size ladder.
 *
 * Density only, exactly as `controlSize.ts` states the rule — the label and the
 * readout render at their own type tokens at every size.
 *
 * `md` keeps the old component's **20px** thumb, which is what four of the five
 * call sites have been rendering; the track goes 2px → 4px, because a 2px hairline
 * is not a shape you can aim at, and it is the surface the filled/unfilled split
 * has to be legible on. `sm` (16px) is for a config panel row; `lg` (24px) clears
 * the 24px minimum target of WCAG SC 2.5.8 outright.
 */
const TRACK_PX: Record<ControlSize, number> = { sm: 3, md: 4, lg: 6 };
const THUMB_PX: Record<ControlSize, number> = { sm: 16, md: 20, lg: 24 };

const Slider = React.forwardRef<HTMLInputElement, SliderProps>(function Slider({
  label,
  hideLabel = false,
  value: valueProp,
  defaultValue,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  showValue = false,
  formatValue,
  size = 'md',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  const reactId = React.useId();
  const inputId = (rest.id as string | undefined) ?? `${reactId}-slider`;
  /* § 357 — uncontrolled only with `defaultValue` and no `value`. Every call site passes
     `value` (it was required), so the `min` fallback is reached by none of them. */
  const [value, setValue] = useControllableState(valueProp, defaultValue, min);

  /*
    The percentage the FILL is drawn to.

    Clamped, and guarded against `max === min`. Both cases are reachable: Traffic
    Distribution can hold a model at 0 while the input's own `min` is higher (see
    `SliderOwnProps.min`), and `ContainerConfigPanel` computes `minWidth`/`maxWidth`
    from sibling containers, so a row with one container hands over an equal pair.
    Unguarded that is a division by zero and the gradient silently disappears —
    `NaN%` is not a valid colour stop, so the browser drops the whole declaration
    and the track renders transparent.
  */
  const span = max - min;
  const pct = span > 0 ? Math.min(100, Math.max(0, ((value - min) / span) * 100)) : 0;

  const display = formatValue ? formatValue(value) : String(value);

  return (
    <div
      className={withClassName(`tw-flex tw-flex-col tw-gap-1`, className)}
      data-testid={dataTestId ? `${dataTestId}-wrapper` : undefined}
    >
      {/*
        `hideLabel` hides the label ROW, never the name — the name moves to
        `aria-label` on the input below. `sr-only` was considered and rejected for
        the same reason `FieldTitle` keeps the tooltip outside the `<label>`: a
        visually-hidden `<label htmlFor>` still occupies the accessibility tree
        twice over once the input also carries `aria-label`, and the two can drift.
        One name, one source.
      */}
      {!hideLabel && (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          <FieldTitle
            label={label}
            htmlFor={inputId}
            disabled={disabled}
            data-testid={dataTestId ? `${dataTestId}-title` : undefined}
          />
          {showValue && (
            <Typography
              variant="sub-title-label"
              as="span"
              className={disabled ? 'tw-text-ink-disabled' : 'tw-text-ink'}
              data-testid={dataTestId ? `${dataTestId}-value` : undefined}
            >
              {display}
            </Typography>
          )}
        </div>
      )}

      <input
        /*
          `rest` first — the rule Button.tsx sets out. Spread after `onChange`, a
          caller's own native handler would REPLACE the slider's, leaving a control
          that drags visibly and reports nothing.
        */
        {...rest}
        ref={ref}
        id={inputId}
        type="range"
        className={`ds-slider__input ${FOCUS_RING}`}
        /* Read by `slider.css`. Inline because they are per-instance values; the
           colours they feed into are all `--ds-*`, set once in `tokens.css`. */
        style={{
          ['--ds-slider-pct' as string]: `${pct}%`,
          ['--ds-slider-track' as string]: `${TRACK_PX[size]}px`,
          ['--ds-slider-thumb' as string]: `${THUMB_PX[size]}px`,
          ...(rest.style ?? {}),
        }}
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        /*
          The name, when the visible label row is suppressed. When it is NOT
          suppressed the `<label htmlFor>` above is the name and this stays
          undefined — two names for one control is the WCAG 2.5.3 hazard
          `Toggle` was fixed for, and setting both here would reintroduce it.
        */
        aria-label={hideLabel ? label : undefined}
        /*
          Without this a percentage slider announces "33" and a pixel slider
          announces "12". The unit lives in `formatValue`, which is also what the
          visible readout uses, so the two cannot disagree. Omitted entirely when
          there is no formatter — `aria-valuetext="33"` duplicating `aria-valuenow`
          is noise, and some screen readers read both.
        */
        aria-valuetext={formatValue ? display : undefined}
        onChange={(event) => {
          const next = Number(event.target.value);
          setValue(next);
          onChange?.(next, event);
        }}
        data-testid={dataTestId}
      />
    </div>
  );
});

export default Slider;
